import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { IEmojiData } from 'emoji-picker-react';
import * as React from 'react';
import { toast } from 'sonner';
import { SocialMediaPostViewModel } from '../../../../extViewmodels';
import { useFacebookPageAutoCompleteViewModel } from '../../../../queries/Social/useFacebookPageAutoCompleteViewModel';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	getFacebookMentionRanges,
	getFacebookMentionText,
	getFacebookPageLocationLabel,
	getInstagramCaptionCounts,
	getInstagramMentionRanges,
	getMentionSegments,
	INSTAGRAM_CAPTION_LIMITS,
} from '../../../../models/utils/socialUtils';
import { useInstagramUsernameLookupQueries } from '../../../../queries/Social/useInstagramUsernameLookupQueries';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { Pill } from '../../../components-v2/Pill';
import { TextArea } from '../../../components-v2/TextArea';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { AutoCompleteDropdown } from '../../autocomplete/AutoCompleteDropdown';
import { EmojiPickerButton } from '../../texting/EmojiPickerButton';
import { SocialMediaImageSearchAndDisplay } from '../SocialMediaImageSearchAndDisplay';
import { SocialMediaImagesPreviewModal } from '../SocialMediaImagesPreviewModal';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	headerLabel?: string;
	imagesLabel?: string;
	impersonationContext?: ImpersonationContextViewModel;
	messageLabel?: string;
	onPostScheduled?: (postViewModel: SocialMediaPostViewModel) => void;
	post?: SocialMediaPostViewModel;
	readOnly?: boolean;
	styles?: StyleDeclarationValue[];
}

interface IFacebookMentionTrigger {
	end: number;
	query: string;
	start: number;
}

interface IAnchorPosition {
	left: number;
	top: number;
}

interface IHoveredMention {
	facebookMention?: Api.IFacebookPageMention;
	instagramUsername?: string;
	left: number;
	top: number;
}

const FACEBOOK_MENTION_TRIGGER_REGEX = /(^|\s)@(\S{0,2048})$/;
const FACEBOOK_VERIFICATION_BADGES = {
	blue_verified: { color: 'blue', label: 'Verified' },
	gray_verified: { color: 'gray', label: 'Verified' },
} as const;

const FacebookVerificationStatusBadge: React.FC<{ verificationStatus?: string }> = ({ verificationStatus }) => {
	const normalizedStatus = verificationStatus?.trim().toLowerCase();
	const badge = normalizedStatus
		? FACEBOOK_VERIFICATION_BADGES[normalizedStatus as keyof typeof FACEBOOK_VERIFICATION_BADGES]
		: undefined;

	return badge ? <Pill color={badge.color} label={badge.label} size='small' variant='label' /> : null;
};

function getFacebookPageMetaLabel(page: Api.IFacebookPageLookupResult) {
	const handleLabel = page.handle ? `@${page.handle}` : '';
	return [handleLabel, getFacebookPageLocationLabel(page.location)].filter(Boolean).join(' · ');
}

function getFacebookMentionPageDetails(
	mention: Api.IFacebookPageMention,
	pageDetails: Map<string, Api.IFacebookPageLookupResult>
): Api.IFacebookPageLookupResult {
	const searchResult = mention.pageId ? pageDetails.get(mention.pageId) : undefined;
	return {
		...searchResult,
		handle: mention.handle || searchResult?.handle,
		// A Page's vanity URL is its handle, so a mention restored from a draft still links somewhere.
		link: searchResult?.link || (mention.handle ? `https://www.facebook.com/${mention.handle}` : undefined),
		pageId: mention.pageId,
		pageName: mention.pageName || searchResult?.pageName,
	};
}

// Missing lookup results are unknown, not unconfirmed.
function isInstagramUsernameUnconfirmed(
	username: string | undefined,
	lookups: Map<string, Api.IInstagramUsernameLookupResult>
) {
	const lookup = username ? lookups.get(username.toLowerCase()) : undefined;
	return !!lookup && !lookup.isConfirmed;
}

function getFacebookMentionTrigger(value: string, caretIndex: number): IFacebookMentionTrigger | null {
	const textBeforeCaret = value.slice(0, caretIndex);
	const triggerMatch = textBeforeCaret.match(FACEBOOK_MENTION_TRIGGER_REGEX);
	if (!triggerMatch) {
		return null;
	}

	const query = triggerMatch[2] || '';
	return {
		end: caretIndex,
		query,
		start: textBeforeCaret.length - query.length - 1,
	};
}

const TEXT_METRIC_PROPERTIES = [
	'borderBottomWidth',
	'borderLeftWidth',
	'borderRightWidth',
	'borderTopWidth',
	'boxSizing',
	'fontFamily',
	'fontSize',
	'fontStyle',
	'fontVariant',
	'fontWeight',
	'letterSpacing',
	'lineHeight',
	'paddingBottom',
	'paddingLeft',
	'paddingRight',
	'paddingTop',
	'textAlign',
	'textIndent',
	'whiteSpace',
	'wordBreak',
	'wordSpacing',
	'wordWrap',
] as const;

function getTextareaAnchorPosition(textArea: HTMLTextAreaElement): IAnchorPosition {
	const computedStyle = window.getComputedStyle(textArea);
	const mirror = document.createElement('div');
	const marker = document.createElement('span');
	const textBeforeCaret = textArea.value.slice(0, textArea.selectionStart || 0);
	const textAfterCaret = textArea.value.slice(textArea.selectionStart || 0);

	mirror.style.position = 'absolute';
	mirror.style.visibility = 'hidden';
	mirror.style.whiteSpace = 'pre-wrap';
	mirror.style.wordWrap = 'break-word';
	mirror.style.overflow = 'hidden';
	mirror.style.width = computedStyle.width;

	TEXT_METRIC_PROPERTIES.forEach(property => {
		mirror.style[property] = computedStyle[property];
	});

	mirror.textContent = textBeforeCaret;
	marker.textContent = textAfterCaret || '.';
	mirror.appendChild(marker);
	textArea.ownerDocument.body.appendChild(mirror);

	const anchorPosition = {
		left: Math.max(12, marker.offsetLeft - textArea.scrollLeft),
		top: Math.max(12, marker.offsetTop - textArea.scrollTop + marker.offsetHeight),
	};

	textArea.ownerDocument.body.removeChild(mirror);
	return anchorPosition;
}

function getContentChangeRange(previousContent: string, nextContent: string) {
	let start = 0;
	while (
		start < previousContent.length &&
		start < nextContent.length &&
		previousContent[start] === nextContent[start]
	) {
		start += 1;
	}

	let previousEnd = previousContent.length;
	let nextEnd = nextContent.length;
	while (previousEnd > start && nextEnd > start && previousContent[previousEnd - 1] === nextContent[nextEnd - 1]) {
		previousEnd -= 1;
		nextEnd -= 1;
	}

	return {
		nextEnd,
		previousEnd,
		start,
	};
}

function reconcileFacebookMentions(
	previousContent: string,
	nextContent: string,
	facebookMentions: Api.IFacebookPageMention[] | undefined
): Api.IFacebookPageMention[] {
	if (!facebookMentions?.length) {
		return [];
	}

	const { start, previousEnd, nextEnd } = getContentChangeRange(previousContent, nextContent);
	const offsetDelta = nextEnd - previousEnd;

	return facebookMentions
		.map(mention => {
			const mentionText = getFacebookMentionText(mention, previousContent);
			if (!mentionText) {
				return null;
			}

			const mentionStart = mention.contentOffset;
			const mentionEnd = mentionStart + mention.contentLength;
			const overlapsChangedRange = mentionStart < previousEnd && mentionEnd > start;
			if (overlapsChangedRange) {
				return null;
			}

			const nextOffset = mentionStart >= previousEnd ? mentionStart + offsetDelta : mentionStart;
			const actualMentionText = nextContent.substring(nextOffset, nextOffset + mentionText.length);
			if (actualMentionText !== mentionText) {
				return null;
			}

			return {
				...mention,
				contentLength: mentionText.length,
				contentOffset: nextOffset,
			};
		})
		.filter((mention): mention is Api.IFacebookPageMention => Boolean(mention));
}

export const SocialMediaPostContentEditor: React.FC<IProps> = props => {
	const {
		className,
		headerLabel,
		imagesLabel,
		impersonationContext,
		messageLabel,
		onPostScheduled,
		post,
		readOnly,
		styles = [],
	} = props;
	const {
		postContent,
		postImages,
		setContent,
		setImages: _setImages,
		hasVideo,
		overlayLogo,
		hideWhiteBackground,
		logoSize,
		setLogoSize,
		facebookMentions,
		setFacebookMentions,
		postTargets,
		selectedCreator,
	} = useEditSocialMediaPostContext();

	const userSession = useUserSession();
	const account = impersonationContext?.account || userSession.account;
	const isFacebookMentioningEnabled = Boolean(
		account?.features?.socialMedia?.facebookMentionsEnabled &&
			postTargets?.some(target => target.provider === Api.SocialMediaType.Facebook) &&
			!readOnly
	);

	const isInstagramTargeted = Boolean(postTargets?.some(target => target.provider === Api.SocialMediaType.Instagram));
	const isLinkedInTargeted = Boolean(postTargets?.some(target => target.provider === Api.SocialMediaType.LinkedIn));
	const inputRef = React.useRef<HTMLTextAreaElement>(null);
	const textAreaWrapperRef = React.useRef<HTMLDivElement>(null);
	const mentionHighlightRef = React.useRef<HTMLDivElement>(null);
	const [imagePreviewModalOpen, setImagePreviewModalOpen] = React.useState(false);
	const [initialImageUrl, setInitialImageUrl] = React.useState<string>('');
	const [facebookMentionTrigger, setFacebookMentionTrigger] = React.useState<IFacebookMentionTrigger | null>(null);
	const [facebookMentionAnchorPosition, setFacebookMentionAnchorPosition] = React.useState<IAnchorPosition>({
		left: 14,
		top: 36,
	});
	const [hoveredMention, setHoveredMention] = React.useState<IHoveredMention | null>(null);
	const hoveredMentionHideTimeoutRef = React.useRef<number | null>(null);

	// Search-only page details are not saved with the mention.
	const facebookPageDetailsRef = React.useRef(new Map<string, Api.IFacebookPageLookupResult>());
	const facebookPageAutoComplete = useFacebookPageAutoCompleteViewModel({
		enabled: isFacebookMentioningEnabled && !!selectedCreator?.id,
		forUserId: selectedCreator?.id,
		impersonationContext: impersonationContext?.toJs(),
	});

	const mentionSegments = React.useMemo(() => {
		// Facebook ranges win overlaps.
		const facebookRanges = getFacebookMentionRanges(postContent, facebookMentions);
		const instagramRanges = isInstagramTargeted ? getInstagramMentionRanges(postContent) : [];
		return getMentionSegments(postContent, [...facebookRanges, ...instagramRanges]);
	}, [facebookMentions, isInstagramTargeted, postContent]);

	const hasLinkedInStrippedMentions = React.useMemo(
		() =>
			isLinkedInTargeted &&
			[...getFacebookMentionRanges(postContent, facebookMentions), ...getInstagramMentionRanges(postContent)].length >
				0,
		[facebookMentions, isLinkedInTargeted, postContent]
	);
	const hasMentionHighlights = mentionSegments.some(segment => segment.isMention);

	const instagramUsernames = React.useMemo(
		() => mentionSegments.map(segment => segment.instagramUsername).filter(Boolean),
		[mentionSegments]
	);
	const instagramLookups = useInstagramUsernameLookupQueries({
		enabled: isInstagramTargeted && !readOnly && !!selectedCreator?.id,
		forUserId: selectedCreator?.id,
		impersonationContext: impersonationContext?.toJs(),
		usernames: instagramUsernames,
	});
	const instagramCaptionCounts = React.useMemo(() => getInstagramCaptionCounts(postContent), [postContent]);

	const onInputRef = (ref: HTMLTextAreaElement) => {
		inputRef.current = ref;
	};

	const syncMentionHighlightScroll = React.useCallback(() => {
		if (!mentionHighlightRef.current || !inputRef.current) {
			return;
		}

		mentionHighlightRef.current.scrollTop = inputRef.current.scrollTop;
		mentionHighlightRef.current.scrollLeft = inputRef.current.scrollLeft;
	}, []);

	const cancelHoveredMentionHide = React.useCallback(() => {
		if (hoveredMentionHideTimeoutRef.current === null) {
			return;
		}

		window.clearTimeout(hoveredMentionHideTimeoutRef.current);
		hoveredMentionHideTimeoutRef.current = null;
	}, []);

	// Held open briefly so the pointer can travel from the mention into the bubble's own link.
	const hideHoveredMention = React.useCallback(() => {
		cancelHoveredMentionHide();
		hoveredMentionHideTimeoutRef.current = window.setTimeout(() => setHoveredMention(null), 200);
	}, [cancelHoveredMentionHide]);

	// Hit-test the non-interactive highlight layer through the textarea.
	const onTextAreaMouseMove = React.useCallback(
		(e: React.MouseEvent<HTMLTextAreaElement>) => {
			const highlightLayer = mentionHighlightRef.current;
			const wrapper = textAreaWrapperRef.current;
			if (!highlightLayer || !wrapper) {
				return;
			}

			const wrapperRect = wrapper.getBoundingClientRect();
			const hovered = Array.from(highlightLayer.querySelectorAll<HTMLElement>('[data-mention-segment]'))
				.flatMap(element => Array.from(element.getClientRects()).map(rect => ({ element, rect })))
				.find(
					({ rect }) =>
						e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom
				);

			if (!hovered) {
				hideHoveredMention();
				return;
			}

			const segment = mentionSegments[Number(hovered.element.dataset.mentionSegment)];
			if (!segment?.isMention) {
				hideHoveredMention();
				return;
			}

			cancelHoveredMentionHide();
			setHoveredMention({
				facebookMention: segment.facebookMention,
				instagramUsername: segment.instagramUsername,
				left: hovered.rect.left - wrapperRect.left,
				top: hovered.rect.bottom - wrapperRect.top + 6,
			});
		},
		[cancelHoveredMentionHide, hideHoveredMention, mentionSegments]
	);

	const syncFacebookMentionTrigger = React.useCallback(
		(content: string, selectionStart?: number | null) => {
			if (
				!isFacebookMentioningEnabled ||
				selectionStart === null ||
				selectionStart === undefined ||
				!inputRef.current
			) {
				setFacebookMentionTrigger(null);
				return;
			}

			const nextTrigger = getFacebookMentionTrigger(content, selectionStart);
			if (!nextTrigger) {
				setFacebookMentionTrigger(null);
				return;
			}

			setFacebookMentionTrigger(nextTrigger);
			setFacebookMentionAnchorPosition(getTextareaAnchorPosition(inputRef.current));
		},
		[isFacebookMentioningEnabled]
	);

	const syncFacebookMentionContext = React.useCallback(
		(previousContent: string, nextContent: string) => {
			const nextMentions = reconcileFacebookMentions(previousContent, nextContent, facebookMentions);
			const mentionsChanged =
				nextMentions.length !== (facebookMentions || []).length ||
				nextMentions.some((mention, index) => {
					const currentMention = facebookMentions?.[index];
					return (
						!currentMention ||
						currentMention.contentOffset !== mention.contentOffset ||
						currentMention.contentLength !== mention.contentLength ||
						currentMention.pageId !== mention.pageId ||
						currentMention.pageName !== mention.pageName ||
						currentMention.handle !== mention.handle
					);
				});
			if (mentionsChanged) {
				setFacebookMentions(nextMentions);
			}
		},
		[facebookMentions, setFacebookMentions]
	);

	const onTextInputChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const nextContent = e.target.value;
			setContent(nextContent);
			syncFacebookMentionContext(postContent || '', nextContent);
			syncFacebookMentionTrigger(nextContent, e.target.selectionStart);
		},
		[postContent, setContent, syncFacebookMentionContext, syncFacebookMentionTrigger]
	);

	const onTextAreaSelectionChanged = React.useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement> | React.MouseEvent<HTMLTextAreaElement>) => {
			if (!facebookMentionTrigger) {
				return;
			}

			const target = e.target as HTMLTextAreaElement;
			syncFacebookMentionTrigger(target.value, target.selectionStart);
		},
		[facebookMentionTrigger, syncFacebookMentionTrigger]
	);

	const onClickEnlargeImage = (initialUrl: string) => {
		setInitialImageUrl(initialUrl);
		setImagePreviewModalOpen(true);
	};

	const onEmojiClick = React.useCallback(
		(_: React.MouseEvent<Element, MouseEvent>, data: IEmojiData) => {
			if (!inputRef.current) {
				return;
			}

			const previousContent = postContent || '';
			const selectionStart = inputRef.current.selectionStart || 0;
			const nextContent =
				previousContent.substring(0, selectionStart) +
				data.emoji +
				previousContent.substring(inputRef.current.selectionStart);
			const nextSelectionStart = selectionStart + data.emoji.length;

			setContent(nextContent);
			syncFacebookMentionContext(previousContent, nextContent);

			window.requestAnimationFrame(() => {
				inputRef.current?.focus();
				inputRef.current?.setSelectionRange(nextSelectionStart, nextSelectionStart);
				syncFacebookMentionTrigger(nextContent, nextSelectionStart);
			});
		},
		[postContent, setContent, syncFacebookMentionContext, syncFacebookMentionTrigger]
	);

	const insertFacebookPageMention = React.useCallback(
		(page: Api.IFacebookPageLookupResult, start: number, end: number) => {
			if (page.pageId) {
				facebookPageDetailsRef.current.set(page.pageId, page);
			}

			const content = postContent || '';
			const safeStart = Math.max(0, Math.min(start, content.length));
			const safeEnd = Math.max(safeStart, Math.min(end, content.length));
			const mentionText = `@${page.pageName || ''}`;
			const nextContent = content.slice(0, safeStart) + mentionText + content.slice(safeEnd);
			const offsetDelta = mentionText.length - (safeEnd - safeStart);
			const nextSelectionStart = safeStart + mentionText.length;

			setContent(nextContent);
			setFacebookMentions(currentMentions => {
				const nextMentions = (currentMentions || [])
					.filter(mention => {
						const mentionEnd = mention.contentOffset + mention.contentLength;
						return mentionEnd <= safeStart || mention.contentOffset >= safeEnd;
					})
					.map(mention => {
						if (mention.contentOffset >= safeEnd) {
							return {
								...mention,
								contentOffset: mention.contentOffset + offsetDelta,
							};
						}

						return mention;
					});

				return [
					...nextMentions,
					{
						contentLength: mentionText.length,
						contentOffset: safeStart,
						handle: page.handle,
						pageId: page.pageId,
						pageName: page.pageName,
					},
				].sort((left, right) => left.contentOffset - right.contentOffset);
			});
			setFacebookMentionTrigger(null);
			facebookPageAutoComplete.reset();

			window.requestAnimationFrame(() => {
				inputRef.current?.focus();
				inputRef.current?.setSelectionRange(nextSelectionStart, nextSelectionStart);
			});
		},
		[facebookPageAutoComplete, postContent, setContent, setFacebookMentions]
	);

	const onFacebookPageSelected = React.useCallback(
		(page: Api.IFacebookPageLookupResult) => {
			if (!facebookMentionTrigger) {
				return;
			}

			insertFacebookPageMention(page, facebookMentionTrigger.start, facebookMentionTrigger.end);
		},
		[facebookMentionTrigger, insertFacebookPageMention]
	);

	const onValidateSelectedFile = React.useCallback(
		(file: File) => {
			const mimeType = file.type?.toLocaleLowerCase();
			if (!(mimeType.startsWith('video/') || mimeType.startsWith('image/'))) {
				toast.error('Please select a valid image or video file.');
				return false;
			}

			if (mimeType.startsWith('video/') && !hasVideo && postImages?.length > 0) {
				toast.error('This post already has a image. Please remove the image to add a video.');
				return false;
			}

			if (mimeType.startsWith('image/') && hasVideo) {
				toast.error('This post already has a video. Please remove the video to add an image.');
				return false;
			}

			return true;
		},
		[hasVideo, postImages?.length]
	);

	React.useEffect(() => {
		if (isFacebookMentioningEnabled) {
			return;
		}

		setFacebookMentionTrigger(null);
	}, [isFacebookMentioningEnabled]);

	React.useEffect(() => cancelHoveredMentionHide, [cancelHoveredMentionHide]);

	const mentionDropdownSearchQuery = facebookMentionTrigger ? facebookMentionTrigger.query || ' ' : '';
	const normalizedMentionSearch = facebookMentionTrigger?.query?.trim() || '';
	const hoveredMentionPage = hoveredMention?.facebookMention
		? getFacebookMentionPageDetails(hoveredMention.facebookMention, facebookPageDetailsRef.current)
		: null;
	const hoveredInstagramLookup = hoveredMention?.instagramUsername
		? instagramLookups.get(hoveredMention.instagramUsername.toLowerCase())
		: null;
	const hoveredInstagramMetaLabel = [
		hoveredInstagramLookup?.name ? `@${hoveredMention?.instagramUsername}` : '',
		hoveredInstagramLookup?.followersCount ? `${hoveredInstagramLookup.followersCount.toLocaleString()} followers` : '',
	]
		.filter(Boolean)
		.join(' · ');

	return (
		<div className={`${css(styleSheet.container, ...styles)} social-media-post-content-editor ${className || ''}`}>
			<div className={css(styleSheet.headerContainer)}>
				{headerLabel && !readOnly && <div className={css(styleSheet.header)}>{headerLabel}</div>}
			</div>
			<div className={css(styleSheet.composerContainer)}>
				<div className={css(styleSheet.labelContainer)}>
					<div className={`emoji-label-container ${css(styleSheet.labelEmoji)}`}>
						{messageLabel && <label>{messageLabel}</label>}
					</div>
					<div className={css(styleSheet.charCount)}>
						<EmojiPickerButton onEmojiClick={!readOnly ? onEmojiClick : null} popoverPlacement={['left', 'top']} />
						{isInstagramTargeted ? (
							<span
								className={css(
									styleSheet.captionCount,
									instagramCaptionCounts.hashtags > INSTAGRAM_CAPTION_LIMITS.hashtags &&
										styleSheet.captionCountOverLimit
								)}
								title='Instagram allows 30 hashtags per caption'
							>
								{`# ${instagramCaptionCounts.hashtags}/${INSTAGRAM_CAPTION_LIMITS.hashtags}`}
							</span>
						) : null}
						{isInstagramTargeted ? (
							<span
								className={css(
									styleSheet.captionCount,
									instagramCaptionCounts.mentions > INSTAGRAM_CAPTION_LIMITS.mentions &&
										styleSheet.captionCountOverLimit
								)}
								title='Instagram allows 20 @ mentions per caption'
							>
								{`@ ${instagramCaptionCounts.mentions}/${INSTAGRAM_CAPTION_LIMITS.mentions}`}
							</span>
						) : null}
						{postContent?.length > 0 ? `${postContent?.length}/2200` : '2200'}
					</div>
				</div>
				<div className={css(styleSheet.textAreaWrapper)} ref={textAreaWrapperRef}>
					{hasMentionHighlights ? (
						<div
							aria-hidden='true'
							className={css(styleSheet.mentionHighlightLayer, readOnly && styleSheet.mentionHighlightLayerDisabled)}
							ref={mentionHighlightRef}
						>
							{mentionSegments.map((segment, index) =>
								segment.isMention ? (
									<span
										className={css(
											styleSheet.mentionHighlight,
											isInstagramUsernameUnconfirmed(segment.instagramUsername, instagramLookups) &&
												styleSheet.mentionHighlightUnconfirmed
										)}
										data-mention-segment={index}
										data-testid='facebook-mention-highlight'
										key={`mention-${index}`}
									>
										{segment.text}
									</span>
								) : (
									<React.Fragment key={`text-${index}`}>{segment.text}</React.Fragment>
								)
							)}
							{'\n'}
						</div>
					) : null}
					<TextArea
						id='social-media-content'
						disabled={readOnly}
						styles={
							hasMentionHighlights
								? [styleSheet.textAreaContainer, styleSheet.textAreaOverHighlights]
								: [styleSheet.textAreaContainer]
						}
						onChange={onTextInputChanged}
						onClick={onTextAreaSelectionChanged}
						onKeyUp={onTextAreaSelectionChanged}
						onMouseLeave={hasMentionHighlights ? hideHoveredMention : undefined}
						onMouseMove={hasMentionHighlights ? onTextAreaMouseMove : undefined}
						onScroll={syncMentionHighlightScroll}
						ref={onInputRef}
						value={postContent || ''}
					/>
					{hoveredMention?.instagramUsername ? (
						<div
							className={css(styleSheet.mentionBubble)}
							onMouseEnter={cancelHoveredMentionHide}
							onMouseLeave={hideHoveredMention}
							style={{ left: `${hoveredMention.left}px`, top: `${hoveredMention.top}px` }}
						>
							<div className={css(styleSheet.mentionResultNameRow)}>
								<div className={css(styleSheet.mentionResultName)}>
									{hoveredInstagramLookup?.name || `@${hoveredMention.instagramUsername}`}
								</div>
								{hoveredInstagramLookup?.isConfirmed ? (
									<Pill color='blue' label='On Instagram' size='small' variant='label' />
								) : null}
							</div>
							{hoveredInstagramMetaLabel ? (
								<div className={css(styleSheet.mentionResultMeta)}>{hoveredInstagramMetaLabel}</div>
							) : null}
							{isInstagramUsernameUnconfirmed(hoveredMention.instagramUsername, instagramLookups) ? (
								<div className={css(styleSheet.mentionBubbleNote)}>
									We can&apos;t confirm this account, so double-check the spelling. Instagram only tells us about
									business and creator accounts — the mention still posts either way.
								</div>
							) : null}
							<a
								className={css(styleSheet.mentionBubbleLink)}
								href={`https://www.instagram.com/${hoveredMention.instagramUsername}`}
								rel='noopener noreferrer'
								target='_blank'
							>
								View profile
							</a>
						</div>
					) : null}
					{hoveredMention && hoveredMentionPage ? (
						<div
							className={css(styleSheet.mentionBubble)}
							onMouseEnter={cancelHoveredMentionHide}
							onMouseLeave={hideHoveredMention}
							style={{ left: `${hoveredMention.left}px`, top: `${hoveredMention.top}px` }}
						>
							<div className={css(styleSheet.mentionResultNameRow)}>
								<div className={css(styleSheet.mentionResultName)}>{hoveredMentionPage.pageName}</div>
								<FacebookVerificationStatusBadge verificationStatus={hoveredMentionPage.verificationStatus} />
							</div>
							{getFacebookPageMetaLabel(hoveredMentionPage) ? (
								<div className={css(styleSheet.mentionResultMeta)}>{getFacebookPageMetaLabel(hoveredMentionPage)}</div>
							) : null}
							{hoveredMentionPage.link ? (
								<a
									className={css(styleSheet.mentionBubbleLink)}
									href={hoveredMentionPage.link}
									rel='noopener noreferrer'
									target='_blank'
									title={hoveredMentionPage.link}
								>
									View page
								</a>
							) : null}
						</div>
					) : null}
					{isFacebookMentioningEnabled && facebookMentionTrigger ? (
						<AutoCompleteDropdown<Api.IFacebookPageLookupResult, Api.IFacebookPageLookupResult>
							anchor={() => (
								<span
									aria-hidden='true'
									className={css(styleSheet.mentionAnchor)}
									style={{
										left: `${facebookMentionAnchorPosition.left}px`,
										top: `${facebookMentionAnchorPosition.top}px`,
									}}
								/>
							)}
							autoCompleteViewModel={facebookPageAutoComplete}
							contentPositionY='bottom'
							dropdownContentItemsStyles={[styleSheet.mentionDropdown]}
							isOpen={true}
							loaderType='small'
							onItemSelected={onFacebookPageSelected}
							onRenderEmptyResults={() => (
								<div className={css(styleSheet.mentionEmptyState)}>
									{normalizedMentionSearch
										? `No Facebook Pages found for @${normalizedMentionSearch}.`
										: 'Type a Facebook Page handle or paste a Facebook Page URL.'}
								</div>
							)}
							onRenderItem={(item, index, highlighted, onClick) => {
								const metaLabel = getFacebookPageMetaLabel(item);
								return (
									<div
										className={css(styleSheet.mentionResultRow, highlighted && styleSheet.mentionResultRowHighlighted)}
										key={`${item.pageId || item.handle || 'facebook-page'}-${index}`}
									>
										<button
											className={css(styleSheet.mentionResultItem)}
											onMouseDown={e => e.preventDefault()}
											type='button'
											onClick={() => onClick()}
										>
											<div className={css(styleSheet.mentionResultNameRow)}>
												<div className={css(styleSheet.mentionResultName)}>{item.pageName}</div>
												<FacebookVerificationStatusBadge verificationStatus={item.verificationStatus} />
											</div>
											{metaLabel ? <div className={css(styleSheet.mentionResultMeta)}>{metaLabel}</div> : null}
										</button>
										{item.link ? (
											<a
												className={css(styleSheet.mentionResultLink)}
												href={item.link}
												onMouseDown={e => e.stopPropagation()}
												rel='noopener noreferrer'
												target='_blank'
												title={item.link}
											>
												View page
											</a>
										) : null}
									</div>
								);
							}}
							searchQuery={mentionDropdownSearchQuery}
							sectionTitleElement={
								<div className={css(styleSheet.mentionSectionTitle)}>Select the Page you want to mention</div>
							}
						/>
					) : null}
				</div>
				{hasLinkedInStrippedMentions ? (
					<div className={css(styleSheet.mentionDisclaimer)}>
						LinkedIn doesn&apos;t support mentions from Levitate, so your LinkedIn post shows the name on its own,
						without the @. Nobody is notified there.
					</div>
				) : null}
			</div>

			<div className={`add-media-container ${css(styleSheet.mediaContainer)}`}>
				<label>{imagesLabel && !readOnly ? imagesLabel : 'Media'}</label>
				<SocialMediaImageSearchAndDisplay
					onClickEnlargeImage={onClickEnlargeImage}
					hasVideo={hasVideo}
					readOnly={readOnly}
					onValidateSelectedFile={onValidateSelectedFile}
					post={post}
					onPostScheduled={onPostScheduled}
				/>
			</div>

			<SocialMediaImagesPreviewModal
				modalProps={{
					isOpen: imagePreviewModalOpen,
					onRequestClose: () => setImagePreviewModalOpen(false),
				}}
				initialImageUrl={initialImageUrl || ''}
				images={postImages}
				showOverlayLogo={overlayLogo}
				logoUrl={account?.preferences?.logo?.url || ''}
				hideWhiteBackground={hideWhiteBackground}
				logoSize={logoSize}
				onLogoSizeChange={setLogoSize}
			/>
		</div>
	);
};
